import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useGestock } from "../GestockContext";
import {
  useFournisseurs,
  useLivraisonsOrphelines,
  useTerminalStocks,
} from "../useOilmed";
import { fmt, fmtDec, showToast } from "../utils";
import {
  ajusterLP,
  ORPHELINES_LP_DEPUIS,
  watchAjustementsLP,
} from "../oilmed.service";
import { isWithinRange, toJsDate } from "../monthUtils";
import { db } from "../../../../config/firebaseConfig";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";

export default function GLibrePratique() {
  const { terminal, produit, monthRange } = useGestock();
  const { stocks, loading } = useTerminalStocks(terminal);
  const stock = stocks?.[produit];
  const prodLabel = produit === "essence" ? "Super" : "Go";
  const { displayName } = useSelector((state) => state.auth);
  const { liste: fournisseurs, couleur: couleurFournisseur } = useFournisseurs(terminal);
  const repartition = stock?.repartition ?? null;
  // Une carte par fournisseur de la liste (+ ceux ayant déjà un solde)
  const soldesFournisseurs = [
    ...new Set([...fournisseurs, ...Object.keys(repartition?.parFournisseur || {})]),
  ].map((f) => [f, repartition?.parFournisseur[f] ?? 0]);
  const nonAttribue = repartition ? repartition.nonAttribue : stock?.lp ?? 0;

  // Unified movements list (dédouanements + livraisons + ajustements)
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(true);

  // Livraisons sans commande correspondante — comptabilisées dans le LP
  // (cf. approvToJournal) mais absentes des commandes/{id}/livraisons.
  const { orphelines } = useLivraisonsOrphelines(terminal, produit);
  const orphelinesAsMovements = orphelines
    .filter((o) => (o.createdAt?.toDate?.() ?? new Date(0)) >= ORPHELINES_LP_DEPUIS)
    .map((o) => ({
      id: o.id,
      type: "livraison",
      isOrpheline: true,
      date: o.createdAt,
      volumeM3: o.volumeM3 || 0,
      produit: o.produit,
      reference: o.numBL || o.id,
      station: (o.station || "").toUpperCase(),
      fournisseur: o.fournisseur || null,
    }));

  // Ajustement LP
  const [ajustements, setAjustements] = useState([]);
  useEffect(() => {
    const unsub = watchAjustementsLP(terminal, produit, setAjustements);
    return () => unsub();
  }, [terminal, produit]);

  useEffect(() => {
    setMovementsLoading(true);

    // Determine collection based on product: su=essence, go=mazout
    const lotsCollection = produit === "essence" ? "lots_su" : "lots_go";

    // Accumulateurs tenus à jour en continu — recompute() est appelé à
    // chaque événement Firestore (pas un setTimeout unique) pour éviter de
    // perdre des mouvements dont le listener imbriqué résout après coup
    // (cas fréquent avec beaucoup de commandes : les livraisons les plus
    // récentes, ex. celles du mois en cours, arrivaient parfois après le
    // délai fixe de 500ms et disparaissaient silencieusement de la liste).
    const dedouanementsByLot = new Map(); // lotId → movement[]
    const dedouanementUnsubs = new Map(); // lotId → unsub
    const livraisonsByCommande = new Map(); // commandeId → movement[]
    const livraisonUnsubs = new Map(); // commandeId → unsub
    const fournisseurParCommande = new Map(); // commandeId → fournisseur (modifiable)
    let ajustementsList = [];

    const toMs = (v) => v?.toDate?.()?.getTime?.() ?? (v instanceof Date ? v.getTime() : 0);

    const recompute = () => {
      const all = [
        ...Array.from(dedouanementsByLot.values()).flat(),
        ...Array.from(livraisonsByCommande.values())
          .flat()
          .map((l) => ({ ...l, fournisseur: fournisseurParCommande.get(l.commandeId) || null })),
        ...ajustementsList,
      ];
      const seen = new Set();
      const unique = all.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });
      unique.sort((a, b) => toMs(b.date) - toMs(a.date));

      setMovements(unique);
      setMovementsLoading(false);
    };

    // Watch lots collection for dedouanements
    const lotsRef = collection(db, "terminaux", terminal, lotsCollection);
    const unsubLots = onSnapshot(
      lotsRef,
      (lotsSnap) => {
        const currentLotIds = new Set(lotsSnap.docs.map((d) => d.id));
        for (const [lotId, unsub] of dedouanementUnsubs) {
          if (!currentLotIds.has(lotId)) {
            unsub();
            dedouanementUnsubs.delete(lotId);
            dedouanementsByLot.delete(lotId);
          }
        }

        lotsSnap.forEach((lotDoc) => {
          if (dedouanementUnsubs.has(lotDoc.id)) return;
          const lotData = lotDoc.data();
          const numeroPOT =
            lotData.numeroPOT || lotData.bl || lotData.pot || lotDoc.id;

          const dedouanementRef = collection(
            db,
            "terminaux",
            terminal,
            lotsCollection,
            lotDoc.id,
            "dedouanements",
          );
          const unsub = onSnapshot(
            dedouanementRef,
            (dedouanementSnap) => {
              dedouanementsByLot.set(
                lotDoc.id,
                dedouanementSnap.docs.map((d) => {
                  const data = d.data();
                  return {
                    id: d.id,
                    type: "dedouanement",
                    date: data.date || data.createdAt,
                    volumeM3: data.volumeM3 || 0,
                    produit: data.produit || produit,
                    reference: data.reference,
                    terminal,
                    pots: [numeroPOT],
                    lotId: lotDoc.id,
                    // Fournisseur du lot dédouané
                    fournisseur: String(lotData.fournisseur || "").trim().toUpperCase() || null,
                  };
                }),
              );
              recompute();
            },
            (err) => console.warn("[GLibrePratique] dedouanements:", err.code),
          );
          dedouanementUnsubs.set(lotDoc.id, unsub);
        });
        recompute();
      },
      (err) => console.warn("[GLibrePratique] lots:", err.code),
    );

    // Watch commandes for livraisons (top-level collection)
    const commandesRef = query(
      collection(db, "commandes"),
      where("terminal", "==", terminal),
      where("produit", "==", produit),
    );
    const unsubCommandes = onSnapshot(
      commandesRef,
      (commandesSnap) => {
        const currentCmdIds = new Set(commandesSnap.docs.map((d) => d.id));
        commandesSnap.docs.forEach((d) =>
          fournisseurParCommande.set(d.id, d.data().fournisseur || null),
        );
        for (const [cmdId, unsub] of livraisonUnsubs) {
          if (!currentCmdIds.has(cmdId)) {
            unsub();
            livraisonUnsubs.delete(cmdId);
            livraisonsByCommande.delete(cmdId);
          }
        }

        commandesSnap.forEach((commandeDoc) => {
          if (livraisonUnsubs.has(commandeDoc.id)) return;
          const commandeData = commandeDoc.data();

          const livraisonsRef = collection(
            db,
            "commandes",
            commandeDoc.id,
            "livraisons",
          );
          const unsub = onSnapshot(
            livraisonsRef,
            (livraisonsSnap) => {
              livraisonsByCommande.set(
                commandeDoc.id,
                livraisonsSnap.docs.map((d) => {
                  const l = d.data();
                  return {
                    id: d.id,
                    type: "livraison",
                    date: l.date || l.createdAt,
                    volumeM3: l.volumeM3 || 0,
                    produit,
                    reference: commandeData.numBL || commandeDoc.id,
                    station: commandeData.station,
                    commandeId: commandeDoc.id,
                  };
                }),
              );
              recompute();
            },
            (err) => console.warn("[GLibrePratique] livraisons:", err.code),
          );
          livraisonUnsubs.set(commandeDoc.id, unsub);
        });
        recompute();
      },
      (err) => console.warn("[GLibrePratique] commandes:", err.code),
    );

    // Watch ajustements LP
    const ajustRef = query(
      collection(db, "terminaux", terminal, "ajustements_lp"),
      orderBy("createdAt", "desc"),
    );
    const unsubAjust = onSnapshot(
      ajustRef,
      (ajustSnap) => {
        ajustementsList = ajustSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((a) => a.produit === produit)
          .map((a) => ({
            id: a.id,
            type: "ajustement",
            kind: a.kind === "achat" ? "achat" : "ajustement",
            date: a.createdAt,
            volumeM3: a.deltaM3,
            note: a.note,
            userName: a.userName,
            fournisseur: a.fournisseur || null,
          }));
        recompute();
      },
      (err) => console.warn("[GLibrePratique] ajustements:", err.code),
    );

    return () => {
      unsubLots();
      unsubCommandes();
      unsubAjust();
      for (const unsub of dedouanementUnsubs.values()) unsub();
      for (const unsub of livraisonUnsubs.values()) unsub();
    };
  }, [terminal, produit]);

  // Vue mensuelle — le sélecteur de mois global filtre les mouvements affichés
  const monthMovements = [...movements, ...orphelinesAsMovements]
    .filter((m) => isWithinRange(m.date, monthRange))
    .sort((a, b) => (toJsDate(b.date)?.getTime() ?? 0) - (toJsDate(a.date)?.getTime() ?? 0));
  const monthDedouanements = monthMovements.filter((m) => m.type === "dedouanement");
  const monthLivraisons = monthMovements.filter((m) => m.type === "livraison");
  const monthAjustements = ajustements.filter((a) => isWithinRange(a.date, monthRange));

  const totalDedouane = monthDedouanements.reduce((sum, d) => sum + d.volumeM3, 0);
  const totalLivraison = monthLivraisons.reduce((sum, l) => sum + l.volumeM3, 0);

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-title">
          Libre <span>Pratique</span>
        </div>
        <div className="page-sub">
          {"// "}
          {terminal} · {prodLabel.toUpperCase()} — DISPONIBLE POUR LIVRAISONS
        </div>
      </div>

      {/* ── Libre pratique par fournisseur (total si aucun fournisseur défini) ── */}
      <div className="kpi-grid">
        {soldesFournisseurs.length > 0 ? (
          // Cartes triées du plus grand solde au plus petit (Non attribué compris)
          [
            ...soldesFournisseurs.map(([f, v]) => ({ f, v })),
            ...(!loading && Math.abs(nonAttribue) > 0.0005
              ? [{ f: "Non attribué", v: nonAttribue, nonAttribue: true }]
              : []),
          ]
            .sort((a, b) => b.v - a.v)
            .map(({ f, v, nonAttribue: na }) =>
              na ? (
                <div
                  key="__non_attribue"
                  className="kpi-card"
                  style={{ "--kpi-color": "var(--gold)" }}
                  title="Stock sans fournisseur : à répartir via les soldes d'ouverture (Paramètres), ou livraisons sans commande / commandes sans fournisseur à régulariser"
                >
                  <div className="kpi-label">Non attribué</div>
                  <div className="kpi-value">{fmt(v)}</div>
                  <div className="kpi-unit">m³ sans fournisseur</div>
                </div>
              ) : (
                <div key={f} className="kpi-card" style={{ "--kpi-color": couleurFournisseur(f) }}>
                  <div className="kpi-label">{f}</div>
                  <div className="kpi-value" style={v < 0 ? { color: "var(--red)" } : undefined}>
                    {loading ? "…" : fmt(v)}
                  </div>
                  <div className="kpi-unit">m³ disponibles</div>
                </div>
              ),
            )
        ) : (
          <div className="kpi-card" style={{ "--kpi-color": "var(--blue)" }}>
            <div className="kpi-label">Stock libre pratique actuel</div>
            <div className="kpi-value">{loading ? "…" : fmt(stock?.lp)}</div>
            <div className="kpi-unit">
              m³ — ajoutez les fournisseurs dans Paramètres
            </div>
          </div>
        )}
      </div>

      {/* ── Achat et ajustement LP : deux boîtes séparées, fermées par défaut ── */}
      {["achat", "ajustement"].map((kind) => (
        <LPMouvementBox
          key={kind}
          kind={kind}
          terminal={terminal}
          produit={produit}
          prodLabel={prodLabel}
          displayName={displayName}
          fournisseurs={fournisseurs}
          historique={monthAjustements.filter((a) =>
            kind === "achat" ? a.kind === "achat" : a.kind !== "achat",
          )}
        />
      ))}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">📊 Mouvements Libre Pratique</span>
          <span className="badge badge-blue">
            {terminal} · {prodLabel}
          </span>
        </div>

        {movementsLoading ? (
          <div
            style={{
              padding: 20,
              textAlign: "center",
              color: "var(--muted)",
            }}
          >
            Chargement...
          </div>
        ) : monthDedouanements.length === 0 && monthLivraisons.length === 0 ? (
          <div
            style={{
              padding: 20,
              textAlign: "center",
              color: "var(--muted)",
            }}
          >
            Aucun mouvement
          </div>
        ) : (
          <div>
            {/* Summary */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                padding: "12px 14px",
                borderBottom: "1px solid var(--border)",
                background: "var(--bg)",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>
                  Total Dédouanés
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--green)",
                  }}
                >
                  +{fmt(totalDedouane)} m³
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>
                  Total Livrés
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--red)",
                  }}
                >
                  -{fmt(totalLivraison)} m³
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>
                  Libre Pratique
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--blue)",
                  }}
                >
                  {loading ? "…" : fmt(stock?.lp)} m³
                </div>
              </div>
            </div>

            {/* Unified Movements Timeline */}
            {monthMovements.map((m, index) => {
              const isDedouanement = m.type === "dedouanement";
              const isAchat = m.type === "ajustement" && m.kind === "achat";
              const isAjustement = m.type === "ajustement" && !isAchat;
              const uniqueKey = `${m.type}_${m.lotId || m.commandeId || ""}_${m.id}`;
              const badgeColor = isDedouanement
                ? "var(--green)"
                : isAchat
                ? "var(--purple)"
                : isAjustement
                ? "var(--blue)"
                : m.isOrpheline && !m.fournisseur
                ? "var(--gold)"
                : m.fournisseur
                ? couleurFournisseur(m.fournisseur)
                : "var(--red)";
              const sign =
                isDedouanement || isAchat || (isAjustement && m.volumeM3 >= 0)
                  ? "+"
                  : "-";
              const valueColor = isDedouanement
                ? "var(--green)"
                : isAchat
                ? "var(--purple)"
                : isAjustement
                ? m.volumeM3 >= 0 ? "var(--blue)" : "var(--red)"
                : "var(--red)";

              return (
                <div
                  key={uniqueKey}
                  style={{
                    padding: "12px 14px",
                    borderBottom:
                      index < monthMovements.length - 1
                        ? "1px solid var(--border)"
                        : "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          background: badgeColor,
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                        }}
                      >
                        {isDedouanement
                          ? `DÉDOUANÉ${m.fournisseur ? ` ${m.fournisseur}` : ""}`
                          : isAchat
                          ? `ACHAT LP${m.fournisseur ? ` ${m.fournisseur}` : ""}`
                          : isAjustement
                          ? `AJUSTEMENT${m.fournisseur ? ` ${m.fournisseur}` : ""}`
                          : m.isOrpheline
                          ? `${m.fournisseur || "LIVRAISON"} ⚠`
                          : m.fournisseur || "LIVRAISON"}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--muted)" }}>
                        {isDedouanement
                          ? m.reference
                          : isAchat || isAjustement
                          ? m.note || m.userName || ""
                          : m.isOrpheline
                          ? `BL ${m.reference} — commande introuvable${m.station ? ` · ${m.station}` : ""}`
                          : `${m.reference} → ${m.station}`}
                      </span>
                    </div>
                    {/* Show POT numbers for dedouanements */}
                    {isDedouanement && m.pots && m.pots.length > 0 && (
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--text)",
                          fontFamily: "var(--font-mono)",
                          marginBottom: 2,
                        }}
                      >
                        📦 {m.pots.join(", ")}
                      </div>
                    )}
                    {m.date && (
                      <div style={{ fontSize: 10, color: "var(--muted)" }}>
                        {new Date(
                          m.date.seconds
                            ? m.date.seconds * 1000
                            : new Date(m.date).getTime(),
                        ).toLocaleDateString("fr-FR")}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 14,
                      fontWeight: 600,
                      color: valueColor,
                    }}
                  >
                    {sign}{fmt(Math.abs(m.volumeM3))} m³
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const BOX_CONFIG = {
  achat: {
    titre: "🛒 Achat Libre Pratique",
    aide: "Volume acheté directement en libre pratique — ajouté au stock actuel.",
    labelVolume: "Volume acheté (m³)",
    placeholderVolume: "Ex: 100",
    bouton: "Enregistrer l'achat",
    badge: "ACHAT LP",
    couleur: "var(--purple)",
  },
  ajustement: {
    titre: "⚖️ Ajustement Libre Pratique",
    aide: "La valeur saisie sera directement ajoutée au stock libre pratique actuel.",
    labelVolume: "Ajustement (m³)",
    placeholderVolume: "+0,200 ou -0,500",
    labelNote: "Raison (optionnel)",
    placeholderNote: "Ex: Écart livraison BL 1656",
    bouton: "Appliquer l'ajustement",
    badge: "AJUSTEMENT",
    couleur: "var(--blue)",
  },
};

function parseAjDelta(val) {
  const raw = String(val || "").trim().replace(/[\s ]/g, "");
  const norm = raw.includes(",") ? raw.replace(/\./g, "").replace(",", ".") : raw;
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
}

/** Boîte repliable (fermée par défaut) : formulaire + historique d'un type. */
function LPMouvementBox({ kind, terminal, produit, prodLabel, displayName, fournisseurs, historique }) {
  const cfg = BOX_CONFIG[kind];
  const [open, setOpen] = useState(false);
  // Fournisseurs définis (Paramètres) : un achat est toujours attribué à un
  // fournisseur, un ajustement peut rester « Non attribué » (valeur "").
  const [fournisseur, setFournisseur] = useState("");
  const avecFournisseur = fournisseurs.length > 0;
  const fournisseurManquant = avecFournisseur && kind === "achat" && !fournisseur;
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    let d = parseAjDelta(delta);
    if (!d) return showToast("Valeur invalide", "error");
    // Un achat en libre pratique est toujours une entrée positive.
    if (kind === "achat") d = Math.abs(d);
    setSubmitting(true);
    try {
      await ajusterLP({
        terminalId: terminal,
        produit,
        deltaM3: d,
        note: note || (kind === "achat" ? "Achat en libre pratique" : ""),
        date,
        userName: displayName || "",
        kind,
        fournisseur: avecFournisseur ? fournisseur || null : null,
      });
      showToast(
        kind === "achat"
          ? `Achat de ${fmtDec(d, 3)} m³ enregistré ✔`
          : `Ajustement de ${d > 0 ? "+" : ""}${fmtDec(d, 3)} m³ appliqué ✔`,
      );
      setDelta("");
      setNote("");
      setDate(new Date().toISOString().slice(0, 10));
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div
        className="card-header"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
      >
        <span className="card-title">
          <span style={{ display: "inline-block", width: 14, color: "var(--muted)" }}>
            {open ? "▾" : "▸"}
          </span>
          {cfg.titre}
        </span>
        <span className="badge badge-blue">{terminal} · {prodLabel}</span>
      </div>

      {open && (
        <>
          <div style={{ padding: "14px 14px 0" }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>{cfg.aide}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              {avecFournisseur && (
                <div className="form-group" style={{ flex: "1 1 140px", marginBottom: 0 }}>
                  <label>Fournisseur</label>
                  <select value={fournisseur} onChange={(e) => setFournisseur(e.target.value)}>
                    <option value="">{kind === "achat" ? "— Choisir —" : "Non attribué"}</option>
                    {fournisseurs.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group" style={{ flex: "1 1 120px", marginBottom: 0 }}>
                <label>{cfg.labelVolume}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={delta}
                  placeholder={cfg.placeholderVolume}
                  onChange={(e) => setDelta(e.target.value)}
                />
              </div>
              {cfg.labelNote && (
                <div className="form-group" style={{ flex: "2 1 180px", marginBottom: 0 }}>
                  <label>{cfg.labelNote}</label>
                  <input
                    type="text"
                    value={note}
                    placeholder={cfg.placeholderNote}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              )}
              <div className="form-group" style={{ flex: "1 1 120px", marginBottom: 0 }}>
                <label>Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
            </div>
            <button
              className="btn btn-green btn-full"
              style={{ marginBottom: 14 }}
              disabled={!parseAjDelta(delta) || fournisseurManquant || submitting}
              onClick={handleSubmit}
            >
              {submitting ? "En cours…" : cfg.bouton}
            </button>
          </div>

          {historique.length > 0 && (
            <div style={{ borderTop: "1px solid var(--border)" }}>
              <div style={{ padding: "8px 14px 4px", fontFamily: "var(--font-mono)", fontSize: 8, color: "var(--muted)", letterSpacing: 2, textTransform: "uppercase" }}>
                Historique {kind === "achat" ? "achats" : "ajustements"}
              </div>
              {historique.map((aj) => (
                <div key={aj.id} style={{ padding: "8px 14px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ background: cfg.couleur, color: "white", padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 600 }}>
                        {cfg.badge}
                        {aj.fournisseur ? ` ${aj.fournisseur}` : ""}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text)" }}>{aj.date}</span>
                    </div>
                    {aj.note && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{aj.note}</div>}
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600, color: aj.deltaM3 >= 0 ? "var(--green)" : "var(--red)", whiteSpace: "nowrap" }}>
                    {aj.deltaM3 >= 0 ? "+" : ""}{fmtDec(aj.deltaM3, 3)} m³
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
